import { Injectable } from '@angular/core';

import { MessageType } from '@cds/push';
import { PatchScoreboardCommand } from '@cds/push/fixture-commands';
import { ScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { BaseEventModel } from '@frontend/sports/betting-offer/feature/model';
import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { CdsPushService, CdsSubscription, ScoreboardSubscription } from '@frontend/sports/content-distribution/feature';
import { BaseSubscriptionService, MessageCallback, MessageHandler } from '@frontend/sports/event-subscription/feature/base-subscription';
import { applyPatch } from 'fast-json-patch';

@Injectable({ providedIn: 'root' })
export class ScoreboardSubscriptionService<T extends BaseEventModel> extends BaseSubscriptionService<T> {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private scoreboardFactory: ScoreboardFactory,
        push: CdsPushService,
        protected connection: ConnectionConfig,
        loggerFactory: LoggerFactory,
    ) {
        super(push);
        this.logger = loggerFactory.getLogger('FullScoreboardSubscriptionService');
    }

    protected getContextSubscriptions(event: T, callback: MessageCallback): CdsSubscription[] {
        return [new ScoreboardSubscription(event.offerContext, event.id, this.connection.culture, callback)];
    }

    protected getHandlers(): Map<MessageType, MessageHandler<T>> {
        const handlers = new Map<MessageType, MessageHandler<T>>();

        handlers.set(MessageType.ScoreboardPatch, (evt, cmd) => this.updateScoreboard(evt, cmd as PatchScoreboardCommand));

        return handlers;
    }

    private updateScoreboard(event: T, command: PatchScoreboardCommand): T {
        if (!event.fixtureScoreboard) {
            const error = new Error(`Couldn't update full scoreboard on event: ${event.id} because 'fixtureScoreboard' was not present.`);
            this.logger.error(error, 'Error while updating scoreboard');
            throw error;
        }

        applyPatch(event.fixtureScoreboard, command.patch.operations);
        event.scoreboard = this.scoreboardFactory.createScoreboard(event.fixtureScoreboard, event);

        return event;
    }
}
